import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import ReportRoundedIcon from '@mui/icons-material/ReportRounded';
import SelfImprovementRoundedIcon from '@mui/icons-material/SelfImprovementRounded';
import PetsRoundedIcon from '@mui/icons-material/PetsRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import CircleRoundedIcon from '@mui/icons-material/CircleRounded';
import DiamondRoundedIcon from '@mui/icons-material/DiamondRounded';
import { ProgressAttributeRule, ProgressCategoryRule } from './types';

// Shared row rendering for the two non-type rules. Category rules are hard
// caps the healer controls by tagging; attribute rules are advisory targets
// derived from session activity. Both render as icon + name + meter + status,
// but their status vocabularies differ deliberately.

export type LimitStatus = 'OK' | 'CLOSE' | 'AT_CAP' | 'NEED_MORE' | 'MET';

interface LimitRowBaseProps {
  label: string;
  icon: React.ReactNode;
  approved: number;
  boundary: number;
  boundaryLabel: string;   // e.g. "max", "target"
  status: LimitStatus;
  detail?: string;         // small helper text below the bar (optional)
}

const STATUS_COPY: Record<LimitStatus, string> = {
  OK: 'Room to spare',
  CLOSE: 'Approaching limit',
  AT_CAP: 'Limit reached',
  NEED_MORE: 'Recommended',
  MET: 'Target reached',
};

const STATUS_COLOR: Record<LimitStatus, 'primary' | 'warning' | 'error' | 'success'> = {
  OK: 'primary',
  CLOSE: 'warning',
  AT_CAP: 'error',
  NEED_MORE: 'primary',
  MET: 'success',
};

const LimitRowBase: React.FC<LimitRowBaseProps> = ({
  label, icon, approved, boundary, boundaryLabel, status, detail,
}) => {
  const safeBoundary = Math.max(1, boundary);
  const pct = Math.min(100, Math.round((approved / safeBoundary) * 100));
  const color = STATUS_COLOR[status];
  const isCap = status === 'AT_CAP' || status === 'CLOSE';

  return (
    <Box sx={{ pl: 4, pr: 3, pb: 1.25 }} aria-label={`${label}: ${approved} of ${boundary} ${boundaryLabel}`}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 0.75 }}>
        <Box sx={{ color: 'rgba(255,255,255,0.75)', display: 'flex' }}>{icon}</Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 500, flex: 1 }}>
          {label}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
          {approved} / {boundary} {boundaryLabel}
        </Typography>
      </Box>
      <LinearProgress
        variant='determinate'
        value={pct}
        color={color}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: 'rgba(255,255,255,0.08)',
          '& .MuiLinearProgress-bar': { borderRadius: 3 },
        }}
        aria-valuenow={approved}
        aria-valuemin={0}
        aria-valuemax={boundary}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 0.5 }}>
        {isCap && <ReportRoundedIcon sx={{ fontSize: 13, color: color === 'error' ? '#f44336' : '#ffa726' }} />}
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, lineHeight: 1.3 }}>
          {STATUS_COPY[status]}{detail ? ` · ${detail}` : ''}
        </Typography>
      </Box>
    </Box>
  );
};

// -------- Category variant (hard cap) --------

// Name is nullable in the database, so it can arrive empty despite the type.
const categoryIcon = (name?: string | null): React.ReactNode => {
  const lower = (name || '').toLowerCase();
  if (lower.includes('self')) return <SelfImprovementRoundedIcon fontSize='small' />;
  if (lower.includes('animal') || lower.includes('pet')) return <PetsRoundedIcon fontSize='small' />;
  if (lower.includes('client') || lower.includes('person')) return <PersonRoundedIcon fontSize='small' />;
  return <CircleRoundedIcon fontSize='small' />;
};

export const categoryStatus = (rule: ProgressCategoryRule): LimitStatus => {
  if (rule.is_at_cap) return 'AT_CAP';
  return rule.max_count > 0 && rule.approved / rule.max_count >= 0.8 ? 'CLOSE' : 'OK';
};

interface CategoryLimitRowProps {
  rule: ProgressCategoryRule;
}

export const CategoryLimitRow: React.FC<CategoryLimitRowProps> = ({ rule }) => {
  const status = categoryStatus(rule);
  return (
    <LimitRowBase
      label={rule.category_name || ''}
      icon={categoryIcon(rule.category_name)}
      approved={rule.approved}
      boundary={rule.max_count}
      boundaryLabel='max'
      status={status}
      detail={status === 'AT_CAP' ? undefined : `${rule.remaining_to_max} left`}
    />
  );
};

// -------- Attribute variant (advisory target) --------

const ATTRIBUTE_LABEL: Record<string, string> = {
  IN_PERSON: 'In-person',
  CRYSTAL_USED: 'Laser crystal',
};

const attributeIcon = (attr: string): React.ReactNode => {
  if (attr === 'IN_PERSON') return <PersonRoundedIcon fontSize='small' />;
  if (attr === 'CRYSTAL_USED') return <DiamondRoundedIcon fontSize='small' />;
  return <CircleRoundedIcon fontSize='small' />;
};

interface AttributeLimitRowProps {
  rule: ProgressAttributeRule;
}

export const AttributeLimitRow: React.FC<AttributeLimitRowProps> = ({ rule }) => (
  <LimitRowBase
    label={ATTRIBUTE_LABEL[rule.attribute] ?? rule.attribute}
    icon={attributeIcon(rule.attribute)}
    approved={rule.approved}
    boundary={rule.min_count}
    boundaryLabel='target'
    status={rule.is_met ? 'MET' : 'NEED_MORE'}
    detail={rule.is_met ? undefined : `${rule.remaining} to go`}
  />
);
